/**
 * Expected dispatch date for cart checkout.
 *
 * Business rules (warehouse timezone, usually Europe/Amsterdam):
 * - Orders before cutoff (default 20:00) on a ship day → dispatch today
 * - Orders at/after cutoff → start from tomorrow
 * - Skip non-ship days (weekends) and excluded_dates (holidays, MM-DD)
 * - Fri after cutoff / weekend → next valid workday
 * - If dispatch is today, prefix the label with "Today, …"
 *
 * Config is rebuilt in Liquid from theme settings:
 * `dispatch_timezone`, `dispatch_cutoff`, `dispatch_ship_days`, `dispatch_excluded_dates`.
 */

interface DispatchConfig {
  /** IANA timezone for the warehouse cutoff, e.g. "Europe/Amsterdam" */
  timezone: string;
  /** Same-day ship cutoff as "HH:mm" (24h), e.g. "20:00" */
  cutoff: string;
  /** Weekdays the 3PL ships, lowercase English names */
  ship_days: string[];
  /** Non-ship holidays as "MM-DD" (year-agnostic; update movable feasts yearly) */
  excluded_dates: string[];
}

interface ExpectedDispatchDate {
  /** Localized weekday + day + month, e.g. "Tuesday 4 August" */
  dateLabel: string;
  /** True when the computed dispatch day is the warehouse's current calendar day */
  isToday: boolean;
}

/** Fallback when theme JSON is missing or invalid. Includes 2026 movable NL holidays. */
const DEFAULT_DISPATCH_CONFIG: DispatchConfig = {
  timezone: 'Europe/Amsterdam',
  cutoff: '20:00',
  ship_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
  // Fixed: New Year, King's Day, Christmas. Movable 2026: Easter Mon, Ascension, Pentecost Mon.
  excluded_dates: ['01-01', '04-27', '12-25', '12-26', '04-06', '05-14', '05-25'],
};

/** Index matches Date#getUTCDay() / getDay() (0 = Sunday). */
const WEEKDAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;

/** Convert "HH:mm" to minutes since midnight for easy comparison. */
function parseCutoffMinutes(cutoff: string): number {
  const [hour = '20', minute = '00'] = cutoff.split(':');
  const hours = Number.parseInt(hour, 10);
  const minutes = Number.parseInt(minute, 10);

  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return 20 * 60;
  }

  return hours * 60 + minutes;
}

/**
 * Read calendar + clock parts of `date` as seen in `timezone`.
 * Avoids visitor-local Date getters so cutoff is always warehouse-relative.
 */
function getZonedParts(date: Date, timezone: string) {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    weekday: 'long',
  });

  const parts = formatter.formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? '';

  return {
    year: Number.parseInt(get('year'), 10),
    month: Number.parseInt(get('month'), 10),
    day: Number.parseInt(get('day'), 10),
    hour: Number.parseInt(get('hour'), 10),
    minute: Number.parseInt(get('minute'), 10),
    weekday: get('weekday').toLowerCase(),
  };
}

/**
 * Anchor a Y-M-D calendar day at noon UTC.
 * Noon avoids DST edge cases when deriving weekday / formatting that day.
 */
function calendarDateAtNoonUtc(year: number, month: number, day: number): Date {
  return new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
}

/** Add N calendar days to a Y-M-D triple (handles month/year rollover). */
function addCalendarDays(year: number, month: number, day: number, daysToAdd: number) {
  const date = calendarDateAtNoonUtc(year, month, day);
  date.setUTCDate(date.getUTCDate() + daysToAdd);

  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
  };
}

/** Format as "MM-DD" to match `excluded_dates` entries. */
function toMonthDay(month: number, day: number): string {
  return `${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
}

/** Weekday name for a calendar day, independent of the visitor's timezone. */
function weekdayNameForCalendarDay(year: number, month: number, day: number): string {
  const date = calendarDateAtNoonUtc(year, month, day);
  return WEEKDAY_NAMES[date.getUTCDay()];
}

/**
 * Parse theme setting JSON from the inline `<script type="application/json">`.
 * Invalid / empty input → DEFAULT_DISPATCH_CONFIG (arrays cloned).
 */
function parseDispatchConfig(raw: string | null | undefined): DispatchConfig {
  if (!raw?.trim()) {
    return {
      ...DEFAULT_DISPATCH_CONFIG,
      ship_days: [...DEFAULT_DISPATCH_CONFIG.ship_days],
      excluded_dates: [...DEFAULT_DISPATCH_CONFIG.excluded_dates],
    };
  }

  try {
    const parsed = JSON.parse(raw) as Partial<DispatchConfig>;

    return {
      timezone:
        typeof parsed.timezone === 'string' && parsed.timezone ? parsed.timezone : DEFAULT_DISPATCH_CONFIG.timezone,
      cutoff: typeof parsed.cutoff === 'string' && parsed.cutoff ? parsed.cutoff : DEFAULT_DISPATCH_CONFIG.cutoff,
      ship_days:
        Array.isArray(parsed.ship_days) && parsed.ship_days.length > 0
          ? parsed.ship_days.map((day) => String(day).toLowerCase())
          : [...DEFAULT_DISPATCH_CONFIG.ship_days],
      excluded_dates: Array.isArray(parsed.excluded_dates)
        ? parsed.excluded_dates.map((date) => String(date))
        : [...DEFAULT_DISPATCH_CONFIG.excluded_dates],
    };
  } catch {
    return {
      ...DEFAULT_DISPATCH_CONFIG,
      ship_days: [...DEFAULT_DISPATCH_CONFIG.ship_days],
      excluded_dates: [...DEFAULT_DISPATCH_CONFIG.excluded_dates],
    };
  }
}

/**
 * Core rule engine: given config + "now", return the next valid dispatch day.
 *
 * Steps:
 * 1. Interpret now in warehouse timezone
 * 2. Candidate = today if before cutoff, else tomorrow
 * 3. Walk forward (max 14 days) until weekday ∈ ship_days and MM-DD ∉ excluded_dates
 * 4. Format for the storefront locale; flag isToday for the "Today, …" label
 */
function getExpectedDispatchDate(
  config: DispatchConfig,
  now: Date = new Date(),
  locale: string = document.documentElement.lang || 'en',
): ExpectedDispatchDate {
  const zonedNow = getZonedParts(now, config.timezone);
  const cutoffMinutes = parseCutoffMinutes(config.cutoff);
  const nowMinutes = zonedNow.hour * 60 + zonedNow.minute;
  const shipDays = new Set(config.ship_days.map((day) => day.toLowerCase()));
  const excludedDates = new Set(config.excluded_dates);

  // Start with warehouse "today"
  let candidate = {
    year: zonedNow.year,
    month: zonedNow.month,
    day: zonedNow.day,
  };

  // Past cutoff → same-day ship is closed; try from tomorrow
  if (nowMinutes >= cutoffMinutes) {
    candidate = addCalendarDays(candidate.year, candidate.month, candidate.day, 1);
  }

  // Skip weekends / holidays until a valid ship day (safety cap: 2 weeks)
  for (let i = 0; i < 14; i += 1) {
    const weekday = weekdayNameForCalendarDay(candidate.year, candidate.month, candidate.day);
    const monthDay = toMonthDay(candidate.month, candidate.day);
    const isShipDay = shipDays.has(weekday);
    const isExcluded = excludedDates.has(monthDay);

    if (isShipDay && !isExcluded) {
      break;
    }

    candidate = addCalendarDays(candidate.year, candidate.month, candidate.day, 1);
  }

  const isToday =
    candidate.year === zonedNow.year && candidate.month === zonedNow.month && candidate.day === zonedNow.day;

  // e.g. "mardi 4 août" when lang=fr, "Tuesday 4 August" when lang=en
  const dateLabel = new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(calendarDateAtNoonUtc(candidate.year, candidate.month, candidate.day));

  return { dateLabel, isToday };
}

export default class CartDispatch extends HTMLElement {
  private date: HTMLParagraphElement | null = null;
  private configScript: HTMLScriptElement | null = null;

  connectedCallback(): void {
    this.date = this.querySelector('[data-cart-dispatch-ref="date"]');
    this.configScript = this.querySelector('[data-cart-dispatch-ref="config"]');
    this.render();
  }

  /** Fill the date node once on connect using Liquid-provided label templates. */
  private render(): void {
    if (!this.date) return;

    const config = parseDispatchConfig(this.configScript?.textContent);
    const { dateLabel, isToday } = getExpectedDispatchDate(config);

    // Templates from locale: "Today, ([date])" vs "([date])"
    const labelTemplate = isToday
      ? (this.date.dataset.labelToday ?? 'Today, ([date])')
      : (this.date.dataset.labelDate ?? '([date])');

    this.date.textContent = labelTemplate.replace('([date])', dateLabel);
  }
}

customElements.define('cart-dispatch', CartDispatch);
